import { FC, useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import clsx from "clsx";
import ResetModal from "../ResetModal";
import Select from "@albertsons/uds/molecule/Select";
import Tooltip from "@albertsons/uds/molecule/Tooltip";
import Popper from "@albertsons/uds/molecule/Popper";
import MenuButton from "@albertsons/uds/atom/component/MenuButton/MenuButton";
import { Eraser } from "lucide-react";
import debounce from "lodash/debounce";
import useWindowBreakpoints from "../../../hooks/useWindowBreakpoints";
import { AppDispatch, RootState } from "../../../store";
import {
  changeDashboardFilters,
  fetchFilterOptions,
  fetchProductDescription,
  fetchCICAndUPCDetails,
} from "../../../store/modules/dashboard";
import {
  resetExpandModeDCStoreFilter,
  resetExpandModeStoreOrderFilter,
} from "../../../store/modules/orderHistory";
import { LoadingIndicator } from "../../../components";
import {
  defaultFilterOptions,
  defaultFilters,
  defaultFiltersLoaded,
} from "../../../constants/filters";
import {
  FilterPageState,
  FilterType,
  IFilter,
  IFilterLoaded,
  IFilterOptions,
} from "../../../types/filters.types";
import { IFilterOption } from "../../../types/dropdown.types";
import { BarcodeType, BarcodeValue } from "../../../types/barcode.types";
import { OptionsGeneric, StrictModifiers } from "@popperjs/core";
import { Screen } from "../../../contexts/WindowBreakpointsContext";
import "./styles.css";

interface IFiltersProps {
  forceScreen?: "extraSmall" | "mobile" | "desktop";
  isRetailOps?: boolean;
  showWarehouseDivision?: boolean;
  showWarehouse?: boolean;
  filterOptionsLoading: IFilterLoaded;
  dashboardFilters: IFilterOptions;
  filterOptions: IFilterOptions;
  loadFilters?: FilterType[];
  setDashboardFilters: (filters: IFilterOptions) => void;
  fetchFilterOptions: (
    type: FilterType | FilterType[],
    query: string,
    pageNumber: number,
    filters: IFilterOptions,
  ) => void;
  fetchProductDescription: (cic: IFilterOption[]) => void;
  fetchCICAndUPCDetails: (
    data: string,
    type: BarcodeType,
    retailStore: IFilterOption[],
  ) => void;
  resetExpandModeDCStoreFilters: () => void;
  resetExpandModeStoreOrderFilters: () => void;
  floatingResetKey: number;
  filtersResetKey: number;
  filtersUpdateKey: number;
  barcodeValue: BarcodeValue | null;
  resetBarcodeValue: () => void;
  filterPageState: FilterPageState;
}

const EraserIcon = ({ onClear }: { onClear: () => void }) => (
  <div className="invisible">
    <Tooltip zIndex={20} anchor="top">
      <Tooltip.Popover>
        <span className="text-md m-2">Clear Selections</span>
      </Tooltip.Popover>
      <Eraser
        className="bg-[#e6e6e6] cursor-pointer"
        size={12}
        onClick={onClear}
      />
    </Tooltip>
  </div>
);

interface FilterSelectProps {
  label: string;
  fieldName: FilterType;
  id?: string;
  screen: Screen;
  filterOptionsLoading: IFilterLoaded;
  filterOptions: IFilterOptions;
  selectedFilterOptions: IFilterOptions;
  filterPageState: FilterPageState;
  filterKeys: Partial<Record<FilterType, number>>;
  onFocusedFilter: (fieldName: FilterType) => void;
  onBlurFilter: (fieldName: FilterType) => void;
  handleSearch: (fieldName: FilterType, query: string) => void;
  handleSelectChange: (items: IFilterOption[], fieldName: FilterType) => void;
  fetchMore: (fieldName: FilterType) => void;
  resetFilter?: (fieldName: FilterType) => void;
  getSelectionPlaceholder?: (fieldName: FilterType) => string;
  multiple?: boolean;
  checkboxes?: boolean;
  itemText?: keyof IFilterOption;
  containerClassName?: string;
}

const getfilterOptions = (
  screen: Screen,
  fetchMore: () => void,
): Partial<OptionsGeneric<StrictModifiers>> => ({
  modifiers: [
    { name: "preventOverflow", enabled: screen.large },
    {
      name: "scrollBottomLogger",
      enabled: true, // always enabled; hasMore + loading guards are inside the callback via ref
      phase: "main",
      fn: () => {},
      effect: ({ state }: { state: { elements: { popper: HTMLElement } } }) => {
        const popper = state.elements.popper;
        let listEl: Element | null = null;
        let handler: ((e: Event) => void) | null = null;

        const attach = () => {
          const el = popper.querySelector(".ReactVirtualized__List");
          if (el && !listEl) {
            listEl = el;
            handler = (e: Event) => {
              const target = e.target as HTMLElement;
              if (
                target.scrollHeight - target.scrollTop - target.clientHeight <=
                100
              ) {
                fetchMore();
              }
            };
            listEl.addEventListener("scroll", handler);
          }
        };

        const observer = new MutationObserver(attach);
        observer.observe(popper, { childList: true, subtree: true });
        attach();

        return () => {
          observer.disconnect();
          if (listEl && handler) {
            listEl.removeEventListener("scroll", handler);
          }
        };
      },
    } as any,
  ],
});

const FilterSelect = ({
  label,
  fieldName,
  id,
  screen,
  filterOptionsLoading,
  filterOptions,
  selectedFilterOptions,
  filterPageState,
  filterKeys,
  onFocusedFilter,
  onBlurFilter,
  handleSearch,
  handleSelectChange,
  fetchMore,
  resetFilter,
  getSelectionPlaceholder,
  multiple = false,
  checkboxes = false,
  itemText = "label" as keyof IFilterOption,
  containerClassName,
}: FilterSelectProps) => {
  // hasMore + loading state tracked in a ref so the scroll handler never needs to be re-registered
  const canFetchMoreRef = useRef(false);
  canFetchMoreRef.current =
    !filterOptionsLoading[fieldName] && filterPageState[fieldName].hasMore;

  // fetchMore callback ref always points to the latest version without changing identity
  const fetchMoreCallbackRef = useRef<() => void>(() => {});
  fetchMoreCallbackRef.current = () => {
    if (canFetchMoreRef.current) {
      fetchMore(fieldName);
    }
  };

  // Stable wrapper — same function reference across ALL renders → Popper effect runs only once
  const stableFetchMore = useRef(() => fetchMoreCallbackRef.current());

  const firstSelected = selectedFilterOptions[fieldName][0];
  const selectValue = multiple
    ? selectedFilterOptions[fieldName]
    : (firstSelected ?? undefined);

  return (
    <div
      className={clsx(
        "relative",
        screen.small
          ? "border-[1px] border-solid border-black px-2 pb-2"
          : "last:[&>div>div]:hover:!visible",
        containerClassName,
      )}
      key={`filter-${fieldName}-${filterKeys[fieldName] ?? 0}`}
    >
      <div
        className={clsx(
          "my-2 flex justify-between items-center group bg-[#e6e6e6]",
          screen.large && "mx-2 pr-2",
        )}
      >
        <div className="text-xs font-['Arial']">{label}</div>
        {screen.large && resetFilter && (
          <EraserIcon onClear={() => resetFilter(fieldName)} />
        )}
      </div>
      {filterOptionsLoading[fieldName] && <LoadingIndicator />}
      <Select<IFilterOption>
        className={clsx(
          "mt-[2px] border border-[#e5e7eb] px-3 text-sm",
          screen.large && "ml-[7px] w-[93%]",
          screen.small && "w-full",
        )}
        placeholder="All"
        {...(multiple && getSelectionPlaceholder
          ? { selectionPlaceholder: getSelectionPlaceholder(fieldName) }
          : {})}
        id={id ?? fieldName}
        items={filterOptions[fieldName]}
        itemText={itemText}
        itemKey="value"
        {...(multiple
          ? {
              multiple: true,
              checkboxes,
              selectAllOption: !filterPageState[fieldName].hasMore,
            }
          : {})}
        search
        size="md"
        noTags
        value={selectValue as any}
        onClick={() => onFocusedFilter(fieldName)}
        onClose={() => onBlurFilter(fieldName)}
        onSearch={(query) => handleSearch(fieldName, query)}
        onChange={(item: any) =>
          multiple
            ? handleSelectChange(item, fieldName)
            : handleSelectChange([item], fieldName)
        }
        options={getfilterOptions(screen, stableFetchMore.current)}
      />
    </div>
  );
};

const RetailOpsFilter = (props: any) => {
  const {
    screen,
    resetFilter,
    filterOptionsLoading,
    filterPageState,
    filterKeys,
    getSelectionPlaceholder,
    filterOptions,
    selectedFilterOptions,
    onFocusedFilter,
    onBlurFilter,
    handleSearch,
    handleSelectChange,
    clearAll,
    fetchMore,
  } = props;

  const commonProps = {
    screen,
    filterOptionsLoading,
    filterOptions,
    selectedFilterOptions,
    filterPageState,
    filterKeys,
    onFocusedFilter,
    onBlurFilter,
    handleSearch,
    handleSelectChange,
    fetchMore,
    resetFilter,
    getSelectionPlaceholder,
  };

  return (
    <>
      <div
        className={clsx(
          "grid",
          screen.small ? "grid-cols-2" : "grid-cols-1 w-[210px]",
        )}
      >
        <FilterSelect
          {...commonProps}
          label="CIC"
          fieldName="cic"
          itemText={screen.small ? "label" : "description"}
          containerClassName={clsx(
            "order-1 pt-1 bg-[#f4d8d8]",
            !screen.small && "h-[84px]",
          )}
        />
        <FilterSelect
          {...commonProps}
          label="UPC"
          fieldName="upc"
          itemText={screen.small ? "label" : "description"}
          multiple
          checkboxes
          containerClassName="order-2 bg-[#f4d8d8]"
        />
        <FilterSelect
          {...commonProps}
          label="Retail Store"
          fieldName="retailStore"
          multiple
          checkboxes
          containerClassName="order-3"
        />
        <FilterSelect
          {...commonProps}
          label="Retail Division"
          fieldName="retailDivision"
          multiple
          checkboxes
          containerClassName="order-4"
        />
      </div>
      {screen.small && (
        <div
          className={clsx(
            "font-['Arial'] flex justify-center items-center py-2",
            screen.isMobile && "text-xs",
          )}
        >
          <button
            type="button"
            className="border-solid border-[1.5px] border-black rounded-full py-2 px-4 w-fit"
            data-testid="clearAll"
            onClick={clearAll}
          >
            Clear All Filters
          </button>
        </div>
      )}
    </>
  );
};

const LocationFilters = (props: any) => {
  const {
    screen,
    showWarehouseDivision,
    showWarehouse,
    resetFilter,
    filterOptionsLoading,
    filterPageState,
    filterKeys,
    getSelectionPlaceholder,
    filterOptions,
    selectedFilterOptions,
    onFocusedFilter,
    onBlurFilter,
    handleSearch,
    handleSelectChange,
    fetchMore,
  } = props;

  const commonProps = {
    screen,
    filterOptionsLoading,
    filterOptions,
    selectedFilterOptions,
    filterPageState,
    filterKeys,
    onFocusedFilter,
    onBlurFilter,
    handleSearch,
    handleSelectChange,
    fetchMore,
    resetFilter,
    getSelectionPlaceholder,
    multiple: true as const,
    checkboxes: true,
  };

  return (
    <div
      className={clsx(
        "grid",
        screen.small ? "grid-cols-2" : "grid-cols-1 w-[210px]",
      )}
    >
      {showWarehouseDivision && (
        <FilterSelect
          {...commonProps}
          label="Warehouse Division"
          fieldName="warehouseDivision"
          id="warehouse-division"
          containerClassName="order-1"
        />
      )}
      {showWarehouse && (
        <FilterSelect
          {...commonProps}
          label="Warehouse"
          fieldName="warehouse"
          containerClassName={screen.small ? "order-2" : "order-3"}
        />
      )}
      <FilterSelect
        {...commonProps}
        label="Retail Division"
        fieldName="retailDivision"
        containerClassName="order-4"
      />
      <FilterSelect
        {...commonProps}
        label="Retail Store"
        fieldName="retailStore"
        containerClassName="order-5"
      />
    </div>
  );
};

const ProductFilters = (props: any) => {
  const {
    screen,
    resetFilter,
    filterOptionsLoading,
    filterPageState,
    filterKeys,
    getSelectionPlaceholder,
    filterOptions,
    selectedFilterOptions,
    onFocusedFilter,
    onBlurFilter,
    handleSearch,
    handleSelectChange,
    fetchMore,
  } = props;

  const commonProps = {
    screen,
    filterOptionsLoading,
    filterOptions,
    selectedFilterOptions,
    filterPageState,
    filterKeys,
    onFocusedFilter,
    onBlurFilter,
    handleSearch,
    handleSelectChange,
    fetchMore,
    resetFilter,
    getSelectionPlaceholder,
    multiple: true as const,
    checkboxes: true,
  };

  return (
    <>
      <div
        className={clsx(
          "grid",
          screen.small ? "grid-cols-2" : "grid-cols-1 w-[210px]",
        )}
      >
        <FilterSelect
          {...commonProps}
          label="Department"
          fieldName="department"
          containerClassName="order-6"
        />
        <FilterSelect
          {...commonProps}
          label="Retail Section"
          fieldName="retailSection"
          containerClassName="order-7"
        />
        <FilterSelect
          {...commonProps}
          label="SMIC"
          fieldName="smic"
          containerClassName="order-8"
        />
        <FilterSelect
          {...commonProps}
          label="UPC"
          fieldName="upc"
          itemText={screen.small ? "label" : "description"}
          containerClassName="order-9 bg-[#f4d8d8]"
        />
      </div>
      <FilterSelect
        {...commonProps}
        label="CIC"
        fieldName="cic"
        multiple={false}
        checkboxes={false}
        itemText={screen.small ? "label" : "description"}
        containerClassName={clsx(
          "order-10 pt-1 bg-[#f4d8d8]",
          !screen.small && "h-[84px] w-[210px]",
        )}
      />
    </>
  );
};

export const Filters: FC<IFiltersProps> = (props) => {
  const {
    forceScreen,
    isRetailOps = false,
    showWarehouseDivision = false,
    showWarehouse = false,
    filterOptionsLoading,
    dashboardFilters,
    filterOptions,
    setDashboardFilters,
    fetchFilterOptions,
    fetchProductDescription,
    fetchCICAndUPCDetails,
    floatingResetKey,
    filtersResetKey,
    filtersUpdateKey,
    barcodeValue,
    loadFilters = [],
    resetBarcodeValue,
    resetExpandModeDCStoreFilters,
    resetExpandModeStoreOrderFilters,
    filterPageState,
  } = props;

  const [focusedFilters, setFocusedFilters] = useState<FilterType[]>([]);
  const [filterKeys, setFilterKeys] = useState<
    Partial<Record<FilterType, number>>
  >({});
  const [selectedFilterOptions, setSelectedFilterOptions] =
    useState<IFilterOptions>(defaultFilterOptions);
  const [isExpandedProductFilter, setIsExpandedProductFilter] = useState(false);
  const [isExpandedLocationFilter, setIsExpandedLocationFilter] =
    useState(false);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);

  const { getScreen } = useWindowBreakpoints();
  const screen = getScreen(forceScreen);

  const filterLoadStateRef = useRef<IFilterLoaded>({ ...defaultFiltersLoaded });
  const focusedFiltersRef = useRef<FilterType[]>([]);
  const searchQueriesRef = useRef<IFilter>({ ...defaultFilters });
  const lastRequestedPageRef = useRef<Partial<Record<FilterType, number>>>({});
  const updatedFiltersRef = useRef(false);

  const productFilterRef = useRef<HTMLButtonElement>(null);
  const locationFilterRef = useRef<HTMLButtonElement>(null);

  const getSelectionPlaceholder = (fieldName: FilterType) => {
    const selectedOptions = selectedFilterOptions[fieldName];
    if (!selectedOptions || selectedOptions.length === 0) {
      return "All";
    }
    if (selectedOptions.length === 1) {
      if ((fieldName === "upc" || fieldName === "cic") && screen.large) {
        return selectedOptions[0].description;
      }
      return selectedOptions[0].label;
    }
    return "Multiple selections";
  };

  const onFocusedFilter = (fieldName: FilterType) => {
    if (!focusedFilters.includes(fieldName)) {
      const updatedFocussedFilters = [...focusedFilters, fieldName];
      focusedFiltersRef.current = updatedFocussedFilters;
      setFocusedFilters(updatedFocussedFilters);
      if (
        !filterLoadStateRef.current[fieldName] ||
        searchQueriesRef.current[fieldName]
      ) {
        filterLoadStateRef.current[fieldName] = true;
        searchQueriesRef.current[fieldName] = "";
        lastRequestedPageRef.current[fieldName] = 0;
        fetchFilterOptions(fieldName, "", 0, dashboardFilters);
      }
    }
  };

  const onBlurFilter = (fieldName: FilterType) => {
    if (focusedFilters.includes(fieldName)) {
      const updatedFocussedFilters = focusedFilters.filter(
        (field) => field !== fieldName,
      );
      focusedFiltersRef.current = updatedFocussedFilters;
      setFocusedFilters(updatedFocussedFilters);
      setFilterKeys((prev) => ({
        ...prev,
        [fieldName]: (prev[fieldName] ?? 0) + 1,
      }));
    }
  };

  const resetOrderHistoryFilters = () => {
    resetExpandModeDCStoreFilters();
    resetExpandModeStoreOrderFilters();
  };

  const handleSearch = (fieldName: FilterType, query: string) => {
    if (focusedFiltersRef.current.includes(fieldName)) {
      searchQueriesRef.current[fieldName] = query;
      lastRequestedPageRef.current[fieldName] = 0;
      fetchFilterOptions(fieldName, query, 0, dashboardFilters);
    }
  };
  const debouncedHandleSearch = debounce(handleSearch, 300);

  const handleSelectChange = (
    items: IFilterOption[],
    fieldName: FilterType,
    reset = false,
  ) => {
    filterLoadStateRef.current = {
      ...defaultFiltersLoaded,
      [fieldName]: !reset,
    };
    const updatedFilterOptions = {
      ...selectedFilterOptions,
      [fieldName]: items,
    };

    if (fieldName === "cic") {
      fetchProductDescription(items);
    }

    if (
      fieldName === "retailStore" ||
      updatedFilterOptions["retailStore"].length > 0
    ) {
      if (loadFilters.length > 0) {
        loadFilters.forEach((field) => {
          filterLoadStateRef.current = {
            ...filterLoadStateRef.current,
            [field]: updatedFilterOptions["retailStore"].length > 0,
          };
        });
        loadFilters.forEach((field) => {
          updatedFilterOptions[field] = [];
        });
        if (updatedFilterOptions["retailStore"].length > 0) {
          setSelectedFilterOptions(updatedFilterOptions);
          fetchFilterOptions(loadFilters, "", 0, updatedFilterOptions);
        } else {
          setSelectedFilterOptions(updatedFilterOptions);

          updatedFiltersRef.current = true;
        }
      }
    } else {
      setSelectedFilterOptions(updatedFilterOptions);

      updatedFiltersRef.current = true;
    }
  };

  const fetchMore = (fieldName: FilterType) => {
    const nextPage = (lastRequestedPageRef.current[fieldName] ?? 0) + 1;
    lastRequestedPageRef.current[fieldName] = nextPage;
    fetchFilterOptions(
      fieldName,
      searchQueriesRef.current[fieldName],
      nextPage,
      dashboardFilters,
    );
  };
  const debouncedFetchMore = debounce(fetchMore, 300);

  const resetCICandUPC = () => {
    filterLoadStateRef.current = { ...defaultFiltersLoaded };
    setSelectedFilterOptions({
      ...selectedFilterOptions,
      upc: defaultFilterOptions.upc,
      cic: defaultFilterOptions.cic,
    });
    updatedFiltersRef.current = true;
    fetchProductDescription([]);
  };

  const resetFilter = (fieldName: FilterType) => {
    handleSelectChange([], fieldName, true);
  };

  const clearAll = () => {
    if (selectedFilterOptions.retailStore.length > 0) {
      setShowClearAllConfirm(true);
      return;
    }
    executeClearAll();
  };

  const executeClearAll = () => {
    filterLoadStateRef.current = { ...defaultFiltersLoaded };
    setSelectedFilterOptions(defaultFilterOptions);
    updatedFiltersRef.current = true;
    fetchProductDescription([]);
    setShowClearAllConfirm(false);
  };

  const executeClearOthers = () => {
    filterLoadStateRef.current = { ...defaultFiltersLoaded };
    const filteredDefaultOptions = { ...defaultFilterOptions };
    loadFilters.forEach((field) => {
      filteredDefaultOptions[field] = selectedFilterOptions[field];
    });

    setSelectedFilterOptions({
      ...filteredDefaultOptions,
      retailStore: selectedFilterOptions.retailStore,
    });
    updatedFiltersRef.current = true;
    fetchProductDescription([]);
    setShowClearAllConfirm(false);
  };

  const onClickProductFilter = (ev: React.MouseEvent<HTMLElement>): void => {
    setIsExpandedProductFilter(!isExpandedProductFilter);
    if (isExpandedLocationFilter) {
      setIsExpandedLocationFilter(false);
    }
  };

  const onBlurProductFilter = (ev: React.FocusEvent<HTMLElement>): void => {
    if (
      !ev.relatedTarget ||
      (!productFilterRef.current?.contains(ev.relatedTarget) &&
        !productFilterRef.current?.nextElementSibling?.contains(
          ev.relatedTarget,
        ))
    ) {
      setIsExpandedProductFilter(false);
    }
  };

  const onClickLocationFilter = (ev: React.MouseEvent<HTMLElement>): void => {
    setIsExpandedLocationFilter(!isExpandedLocationFilter);
    if (isExpandedProductFilter) {
      setIsExpandedProductFilter(false);
    }
  };

  const onBlurLocationFilter = (ev: React.FocusEvent<HTMLElement>): void => {
    if (
      !ev.relatedTarget ||
      (!locationFilterRef.current?.contains(ev.relatedTarget) &&
        !locationFilterRef.current?.nextElementSibling?.contains(
          ev.relatedTarget,
        ))
    ) {
      setIsExpandedLocationFilter(false);
    }
  };

  useEffect(() => {
    if (barcodeValue) {
      filterLoadStateRef.current.cic = barcodeValue.type === "upc";
      filterLoadStateRef.current.upc = barcodeValue.type === "cic";
      fetchCICAndUPCDetails(
        barcodeValue.data,
        barcodeValue.type,
        selectedFilterOptions.retailStore,
      );
      resetOrderHistoryFilters();
      resetBarcodeValue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcodeValue]);

  useEffect(() => {
    if (floatingResetKey) {
      clearAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floatingResetKey]);

  useEffect(() => {
    if (filtersResetKey) {
      filterLoadStateRef.current = { ...defaultFiltersLoaded };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersResetKey]);

  useEffect(() => {
    if (updatedFiltersRef.current) {
      updatedFiltersRef.current = false;
      setDashboardFilters(selectedFilterOptions);
      resetOrderHistoryFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilterOptions]);

  useEffect(() => {
    if (filtersUpdateKey) {
      setSelectedFilterOptions(dashboardFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersUpdateKey]);

  useEffect(() => {
    return () => {
      debouncedFetchMore.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isRetailOps) {
    return (
      <div
        className={clsx(
          "flex flex-col gap-2 px-2 pt-5 pb-4",
          screen.large && "bg-[#d9d9d9]",
        )}
      >
        <RetailOpsFilter
          screen={screen}
          filterOptionsLoading={filterOptionsLoading}
          filterOptions={filterOptions}
          selectedFilterOptions={selectedFilterOptions}
          filterPageState={filterPageState}
          filterKeys={filterKeys}
          resetFilter={resetFilter}
          getSelectionPlaceholder={getSelectionPlaceholder}
          onFocusedFilter={onFocusedFilter}
          onBlurFilter={onBlurFilter}
          handleSearch={debouncedHandleSearch}
          handleSelectChange={handleSelectChange}
          clearAll={clearAll}
          fetchMore={debouncedFetchMore}
        />
        <ResetModal
          showResetModal={showClearAllConfirm}
          closeResetModal={() => setShowClearAllConfirm(false)}
          onReset={executeClearAll}
          title="Clear All Filters"
          content="Are you sure you want to clear location selection as well?"
          cancelLabel="Clear Others"
          confirmLabel="Clear All"
          onCancel={executeClearOthers}
        />
      </div>
    );
  }

  return (
    <>
      {screen.small ? (
        <div className="flex flex-col gap-2.5 mx-1 mt-2.5 mb-4">
          <MenuButton
            ref={productFilterRef}
            className={clsx(
              "w-full mt-[2px] border border-[#e5e7eb] !bg-[#e6e6e6]",
              screen.small ? "text-sm" : "text-xs !pr-1 !w-[166px]",
            )}
            id="product-filter"
            open={isExpandedProductFilter}
            onClick={onClickProductFilter}
            onBlur={onBlurProductFilter}
          >
            <div className="flex items-center justify-between w-[calc(100%_-_24px)]">
              <span className="font-normal truncate text-dark-text group-focus:text-blue-301">
                Item Selection
              </span>
            </div>
          </MenuButton>
          <Popper
            anchor={productFilterRef}
            open={isExpandedProductFilter}
            onBlur={onBlurProductFilter}
            zIndex={10}
          >
            <div
              className="h-fit rounded-lg bg-white shadow-md p-1"
              style={{
                width: productFilterRef.current?.offsetWidth || 336,
              }}
            >
              <ProductFilters
                screen={screen}
                filterOptionsLoading={filterOptionsLoading}
                filterOptions={filterOptions}
                selectedFilterOptions={selectedFilterOptions}
                filterPageState={filterPageState}
                filterKeys={filterKeys}
                resetFilter={resetFilter}
                getSelectionPlaceholder={getSelectionPlaceholder}
                onFocusedFilter={onFocusedFilter}
                onBlurFilter={onBlurFilter}
                handleSearch={debouncedHandleSearch}
                handleSelectChange={handleSelectChange}
                fetchMore={debouncedFetchMore}
              />
            </div>
          </Popper>
          <MenuButton
            ref={locationFilterRef}
            className={clsx(
              "w-full mt-[2px] border border-[#e5e7eb] !bg-[#e6e6e6]",
              screen.small ? "text-sm" : "text-xs !pr-1 !w-[166px]",
            )}
            id="location-filter"
            open={isExpandedLocationFilter}
            onClick={onClickLocationFilter}
            onBlur={onBlurLocationFilter}
          >
            <div className="flex items-center justify-between w-[calc(100%_-_24px)]">
              <span className="font-normal truncate text-dark-text group-focus:text-blue-301">
                Location Selection
              </span>
            </div>
          </MenuButton>
          <Popper
            anchor={locationFilterRef}
            open={isExpandedLocationFilter}
            onBlur={onBlurLocationFilter}
            zIndex={10}
          >
            <div
              className="h-fit rounded-lg bg-white shadow-md p-1"
              style={{
                width: locationFilterRef.current?.offsetWidth || 336,
              }}
            >
              <LocationFilters
                screen={screen}
                showWarehouseDivision={showWarehouseDivision}
                showWarehouse={showWarehouse}
                filterOptionsLoading={filterOptionsLoading}
                filterPageState={filterPageState}
                filterOptions={filterOptions}
                selectedFilterOptions={selectedFilterOptions}
                filterKeys={filterKeys}
                resetFilter={resetFilter}
                getSelectionPlaceholder={getSelectionPlaceholder}
                onFocusedFilter={onFocusedFilter}
                onBlurFilter={onBlurFilter}
                handleSearch={debouncedHandleSearch}
                handleSelectChange={handleSelectChange}
                fetchMore={debouncedFetchMore}
              />
            </div>
          </Popper>
          {screen.small && (
            <div
              className={clsx(
                "font-['Arial'] flex justify-evenly items-center py-2",
                screen.isMobile && "text-xs",
              )}
            >
              <button
                type="button"
                className="border-solid border-[1.5px] border-black rounded-full py-2 px-4 w-fit"
                onClick={resetCICandUPC}
                data-testid="reset"
              >
                Reset CIC/UPC
              </button>
              <button
                type="button"
                className="border-solid border-[1.5px] border-black rounded-full py-2 px-4 w-fit"
                data-testid="clearAll"
                onClick={clearAll}
              >
                Clear All Filters
              </button>
            </div>
          )}
          <ResetModal
            showResetModal={showClearAllConfirm}
            closeResetModal={() => setShowClearAllConfirm(false)}
            onReset={executeClearAll}
            title="Clear All Filters"
            content="Are you sure you want to clear location selection as well?"
            cancelLabel="Clear Others"
            confirmLabel="Clear All"
            onCancel={executeClearOthers}
          />
        </div>
      ) : (
        <div className="bg-[#d9d9d9] px-2 py-5">
          <LocationFilters
            screen={screen}
            showWarehouseDivision={showWarehouseDivision}
            showWarehouse={showWarehouse}
            filterOptionsLoading={filterOptionsLoading}
            filterPageState={filterPageState}
            filterOptions={filterOptions}
            selectedFilterOptions={selectedFilterOptions}
            filterKeys={filterKeys}
            resetFilter={resetFilter}
            getSelectionPlaceholder={getSelectionPlaceholder}
            onFocusedFilter={onFocusedFilter}
            onBlurFilter={onBlurFilter}
            handleSearch={debouncedHandleSearch}
            handleSelectChange={handleSelectChange}
            fetchMore={debouncedFetchMore}
          />
          <ProductFilters
            screen={screen}
            filterOptionsLoading={filterOptionsLoading}
            filterOptions={filterOptions}
            selectedFilterOptions={selectedFilterOptions}
            filterPageState={filterPageState}
            filterKeys={filterKeys}
            resetFilter={resetFilter}
            getSelectionPlaceholder={getSelectionPlaceholder}
            onFocusedFilter={onFocusedFilter}
            onBlurFilter={onBlurFilter}
            handleSearch={debouncedHandleSearch}
            handleSelectChange={handleSelectChange}
            fetchMore={debouncedFetchMore}
          />
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  filterOptionsLoading: state.dashboard.filterOptionsLoading,
  dashboardFilters: state.dashboard.selectedFilterOptions,
  floatingResetKey: state.dashboard.floatingResetKey,
  filtersResetKey: state.dashboard.filtersResetKey,
  filtersUpdateKey: state.dashboard.filtersUpdateKey,
  filterOptions: state.dashboard.filterOptions,
  filterPageState: state.dashboard.filterPageState,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setDashboardFilters: (filters: IFilterOptions) =>
    dispatch(changeDashboardFilters(filters)),
  fetchFilterOptions: (
    type: FilterType | FilterType[],
    query: string,
    pageNumber: number,
    filters: IFilterOptions,
  ) => dispatch(fetchFilterOptions(type, query, pageNumber, filters)),
  fetchProductDescription: (cic: IFilterOption[]) =>
    dispatch(fetchProductDescription(cic)),
  fetchCICAndUPCDetails: (
    data: string,
    type: BarcodeType,
    retailStore: IFilterOption[],
  ) => dispatch(fetchCICAndUPCDetails(data, type, retailStore)),
  resetExpandModeDCStoreFilters: () => dispatch(resetExpandModeDCStoreFilter()),
  resetExpandModeStoreOrderFilters: () =>
    dispatch(resetExpandModeStoreOrderFilter()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Filters);
